import { useDebounce } from '@reactuses/core';
import { StethoscopeIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  getLocalizedSpeciality,
  specialityMatchesQuery,
} from '@virtual-care/isomorphic';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@virtual-care/shadcn';
import { Loading, useData } from '@virtual-care/ui';

import { useLanguage } from '../../i18n/use-language.ts';
import { Header, ProfileCard, SearchComponent } from '../components/header';
import { PriceLine } from '../components/priceline.tsx';
import { SpecialtyCard } from '../components/specialty-card';
import { useUser } from '../firebase';
import { useSetting } from '../hooks/use-settings.tsx';

function Specialties() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [specialty, setSpecialty] = useState<null | {
    id: string;
    name: string;
    description: string;
  }>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  const { data, isLoading, isFetching } = useData('GET /specialities', {
    pageSize: 100,
    page: 1,
    query: debouncedSearchQuery || undefined,
  });
  const { data: identity } = useUser();
  const appointmentFee = useSetting('appointmentFee');

  const records = data?.records;
  const filteredSpecialties = useMemo(() => {
    if (!records) return [];
    if (!searchQuery.trim()) return records;

    return records.filter((specialty) =>
      specialityMatchesQuery(specialty, language, searchQuery),
    );
  }, [language, records, searchQuery]);

  return (
    <>
      <Header>
        <SearchComponent
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('specialties.searchPlaceholder')}
        />
        <ProfileCard />
      </Header>
      <Dialog
        open={specialty !== null}
        onOpenChange={(isOpen) => !isOpen && setSpecialty(null)}
      >
        <div className="mb-8 p-4 md:p-0 md:pe-4">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">
              {t('specialties.pageTitle')}
            </h1>
            {searchQuery && (
              <p className="mt-2 text-sm text-gray-600">
                {t('specialties.searchResultsFor', {
                  value: filteredSpecialties.length,
                  query: searchQuery,
                })}
              </p>
            )}
          </div>
          {filteredSpecialties.length === 0 && !isLoading && !isFetching ? (
            <div className="col-span-full py-12 text-center">
              <div className="mx-auto w-fit rounded-full bg-gray-100 p-4">
                <StethoscopeIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {t('specialties.empty')}
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {searchQuery
                  ? t('specialties.emptySearch', { query: searchQuery })
                  : t('specialties.emptyDescription')}
              </p>
            </div>
          ) : (
            <>
              {(isFetching || isLoading) && (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-3">
                {filteredSpecialties.map((specialty) => (
                  <SpecialtyCard
                    key={specialty.id}
                    specialty={specialty}
                    onClick={() => {
                      const display = getLocalizedSpeciality(
                        specialty,
                        language,
                      );
                      setSpecialty({
                        id: specialty.id,
                        name: display.name,
                        description: display.description,
                      });
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{specialty?.name}</DialogTitle>
            <DialogDescription>{specialty?.description}</DialogDescription>
          </DialogHeader>

          {identity?.userType !== 'B2B' && (
            <PriceLine value={appointmentFee?.value} />
          )}

          <DialogFooter className="mx-auto flex items-center justify-center">
            <Button
              className="bg-tertiary cursor-pointer px-4"
              onClick={() =>
                navigate(`/book-appointment/${specialty?.id}/symptom-checker`)
              }
            >
              {t('home.dialog.bookAppointment')}
            </Button>
            <Button
              className="bg-chart-2 cursor-pointer px-4"
              onClick={() =>
                navigate(`/second-opinion/${specialty?.id}/symptom-checker`)
              }
            >
              {t('home.dialog.secondOpinion')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Specialties;
